/**
 * Shared bale-session pending/turn-order filter (#1889, review LIVE-3/LIVE-4).
 *
 * "Pending on end `endIdx`" means the archer still needs to SHOOT that end
 * (0-based: an archer whose ends_completed === endIdx is standing on it):
 *   - they are on that end (ends_completed === endIdx),
 *   - their draft is incomplete (an archer with a complete draft is done
 *     shooting and only awaits confirmation — navigating a device to them
 *     strands the view on the old end number),
 *   - their round isn't already finished or truncated early
 *     (ends_completed < ends_total).
 *
 * This predicate historically lived in four diverged copies — Score.jsx's
 * pendingArchers memo, nextArcher's fresh re-check, confirmSkip's walk
 * targets, and useBaleSession's initial redirect. #1894 aligned only the
 * confirmSkip copy; this module is the single owner so the sites can never
 * drift apart again.
 *
 * @param {Array} archers - BaleSessionDetail.archers entries
 * @param {object} opts
 * @param {number} opts.endIdx - 0-based end index (== ends_completed of an archer on it)
 * @param {number} opts.arrowsPerEnd - arrows per end for the session format
 * @param {number|null} [opts.excludeArcherId] - archer to exclude (usually the caller's own)
 * @returns {Array} archers still needing to shoot the end (unsorted)
 */
export function archersPendingOnEnd(archers, { endIdx, arrowsPerEnd, excludeArcherId = null }) {
  return (archers || []).filter(
    (a) =>
      (excludeArcherId == null || a.archer_id !== excludeArcherId) &&
      a.ends_completed === endIdx &&
      a.current_draft_arrows < arrowsPerEnd &&
      a.ends_completed < a.ends_total,
  );
}

/**
 * Sort archers by bale position: ascending bale_position (1a, 1b, 2a, 2b…),
 * then unassigned archers alphabetically by display name.
 *
 * Lives here (not in BaleTray) so pure lib code can use it without pulling a
 * component module in; BaleTray re-exports it for its existing importers.
 */
export function baleOrderSort(a, b) {
  const ap = a.bale_position;
  const bp = b.bale_position;
  if (ap == null && bp == null)
    return (a.display_name || "").localeCompare(b.display_name || "");
  if (ap == null) return 1;
  if (bp == null) return -1;
  return ap - bp;
}

/**
 * Scores-for policy (#1889 round 2 / #1898): may THIS device score the given
 * archer's round?
 *
 *   - The session's assigned scorer scores for everyone — walking every round
 *     is the feature.
 *   - Otherwise only unregistered guests, who have no device of their own.
 *     A REGISTERED bale-mate scores on their own device; navigating this
 *     device onto their round silently cross-scores arrows under the wrong
 *     user (the #1898 QA harm).
 *
 * @param {object} archer - BaleSessionDetail.archers entry
 * @param {boolean} isScorer - whether this device's user is the assigned scorer
 */
export function deviceScoresFor(archer, isScorer) {
  return Boolean(isScorer) || !archer.is_registered;
}

/**
 * Confirm-path walk target (#1898): where should this device navigate after a
 * bale end is confirmed?
 *
 * Mirrors the server's `next_pending_round_id` pick (first round with ends
 * left, in bale order) but restricted to rounds this device may score
 * (`deviceScoresFor`). The caller's own round always counts as a valid stop:
 * when it comes first in bale order the device stays put — returning null —
 * exactly like the old `nextRoundId !== round.id` guard. The server value is
 * caller-agnostic (bale_serializer computes one id for every device), so it
 * must never be used for navigation directly.
 *
 * @param {Array} archers - BaleSessionDetail.archers entries (post-confirm)
 * @param {object} opts
 * @param {number} opts.currentRoundId - the round this device is viewing
 * @param {boolean} [opts.isScorer] - whether this device's user is the assigned scorer
 * @returns {number|null} round id to navigate to, or null to stay put
 */
export function nextWalkRoundId(archers, { currentRoundId, isScorer = false } = {}) {
  const candidates = (archers || [])
    .filter((a) => a.ends_completed < a.ends_total)
    .filter((a) => a.round_id === currentRoundId || deviceScoresFor(a, isScorer))
    .sort(baleOrderSort);
  const target = candidates[0];
  return target && target.round_id !== currentRoundId ? target.round_id : null;
}

/**
 * Within-end walk target (#1898 round 3): among archers still needing to
 * SHOOT end `endIdx` (archersPendingOnEnd), where should this device
 * navigate?
 *
 * This is the ONE walk-target computation for every navigation that happens
 * while an end is in progress — the initial bale redirect on mount, the
 * "Next archer" button, and the post-skip hand-off. The QA round-3 bounces
 * were precisely the sites that composed archersPendingOnEnd + baleOrderSort
 * inline without the deviceScoresFor gate; no call site may build its own
 * walk target again.
 *
 * Policy, in order:
 *   1. only archers pending on `endIdx` are candidates;
 *   2. a candidate must be scoreable by this device (`deviceScoresFor`) —
 *      the caller's own round always counts as a valid stop;
 *   3. bale order, optionally rotated so archers after `currentBalePosition`
 *      come first (the Next-archer/skip flavour); pass no position to walk
 *      from the top of the bale (the initial-redirect flavour);
 *   4. returns null to STAY PUT when the winner is the caller's own round or
 *      when no walkable candidate exists (registered mates score on their
 *      own devices — never navigate onto their rounds).
 *
 * @param {Array} archers - BaleSessionDetail.archers entries
 * @param {object} opts
 * @param {number} opts.endIdx - 0-based end index (== ends_completed of an archer on it)
 * @param {number} opts.arrowsPerEnd - arrows per end for the session format
 * @param {number} opts.currentRoundId - the round this device is viewing
 * @param {number|null} [opts.currentBalePosition] - rotate so positions after this come first
 * @param {number|null} [opts.excludeArcherId] - archer to exclude (usually the caller's own)
 * @param {boolean} [opts.isScorer] - whether this device's user is the assigned scorer
 * @returns {number|null} round id to navigate to, or null to stay put
 */
export function pendingWalkRoundId(
  archers,
  {
    endIdx,
    arrowsPerEnd,
    currentRoundId,
    currentBalePosition = null,
    excludeArcherId = null,
    isScorer = false,
  } = {},
) {
  const candidates = archersPendingOnEnd(archers, { endIdx, arrowsPerEnd, excludeArcherId })
    .filter((a) => a.round_id === currentRoundId || deviceScoresFor(a, isScorer))
    .sort(baleOrderSort);
  const afterCurrent = candidates.filter(
    (a) => (a.bale_position ?? Infinity) > (currentBalePosition ?? -1),
  );
  const target = afterCurrent[0] ?? candidates[0];
  return target && target.round_id !== currentRoundId ? target.round_id : null;
}
